/* Zana Action Registry — client side.
   Backend proposes allow-listed actionIds; the dashboard executes them here.

   Phase 1: navigate intents (run instantly).
   Phase 2: read intents (list alerts by priority — computed on-device) and
   WRITE intents (mark alerts reviewed) that MUST go through a confirm tap in
   the dock before executing. The model never writes anything itself.
   Phase 5: money-leak playbook reads/writes — list/flag buses missing refuel,
   draft driver coach notes. */

export type ZanaActionId =
  | 'open_tab'
  | 'open_high_risk_alerts'
  | 'open_fuel_report'
  | 'open_bus'
  | 'open_zana_mode'
  | 'spotlight'
  | 'start_takeover'
  | 'list_alerts_by_priority'
  | 'list_buses_missing_refuel'
  | 'list_parent_dropoff_flags'
  | 'check_roads_with_google'
  | 'mark_alerts_reviewed'
  | 'flag_buses_missing_refuel'
  | 'triage_high_risk'
  | 'ack_missing_refuel'
  | 'unack_missing_refuel'
  | 'draft_driver_coach_note'
  | 'resolve_incidents'
  | 'email_driver'
  | 'list_unsigned_accounts'
  | 'contact_late_bus_parents'
  | 'prepare_school_trip_draft'
  | 'export_monthly_report'
  | 'export_report'
  | 'schedule_report'
  | 'list_report_schedules'
  | 'cancel_report_schedule'
  | 'request_fuel_readings'
  | 'invite_driver'
  | 'delete_bus'
  | 'create_bus'
  | 'suspend_bus'
  | 'suspend_driver'
  | 'apply_station_pump_price'
  | 'test_zana_connection';

export type ZanaTabKey =
  | 'dashboard'
  | 'fleet'
  | 'accounts'
  | 'monthlyReport'
  | 'trips'
  | 'liveMap'
  | 'analytics'
  | 'fuel'
  | 'fuelPrices';

export interface ZanaIntent {
  actionId: ZanaActionId | string;
  params?: {
    tab?: string;
    busId?: string;
    /** open_bus: land on this run in Recent runs (a pinned run from Analytics → Routes, 2026-09-29). */
    runId?: string;
    entityType?: string;
    entityId?: string;
    driverId?: string;
    driverLabel?: string;
    note?: string;
    plate?: string;
    /** check_roads_with_google: which half of the day to compare. */
    side?: string;
    nickname?: string;
    message?: string;
    destination?: string;
    date?: string;
    month?: string;
    format?: string;
    /* Ranged reports (export_report). `preset` carries the words the owner used
     * ("last week"); from/to carry explicit YYYY-MM-DD when they named dates.
     * The server resolves whichever is present — see reports/range.js. */
    preset?: string;
    /** request_fuel_readings — plates to ask; omitted means every active bus. */
    onlyBuses?: string[];
    from?: string;
    to?: string;
    /* Recurring reports (schedule_report). Numeric because they are clamped
     * arithmetic server-side, not free text. `id` cancels one. */
    cadence?: string;
    hourUtc?: number;
    weekday?: number;
    dayOfMonth?: number;
    id?: string;
    inviteRef?: string;
    accountQuery?: string;
    createPlate?: string;
    action?: string;
    /** Lifecycle ops bag for permissioned takeovers (plate / driver keys). */
    ops?: Record<string, string>;
    /** Google Place ID of a fuel station (apply_station_pump_price). */
    placeId?: string;
    pricePerL?: number;
    fuelType?: string;
    stationName?: string;
    /** A named element to glow. Validated server-side against the takeover
     *  registry's allow-list, so an id the page does not carry never arrives. */
    targetId?: string;
    /** A takeover from the registry. The model picks the id; the app owns the steps. */
    scriptId?: string;
    /** The takeover's title, stamped server-side from the registry — chip words
     *  are never free text. */
    title?: string;
    /** A subject to compose a walkthrough from, when there is no named script
     *  for it. The engine builds the steps; this only says what about. */
    topic?: string;
    /** Skip the Just do it / Walk me through panel — owner already consented. */
    skipChooser?: boolean;
    /** With skipChooser: the owner asked for "just do it" — run unattended.
     *  Absent means the guided walk. */
    auto?: boolean;
    /** 2026-09-18: 'jump' lands on the subject and stops — what a direct ask
     *  gets. 'walk' is the narrated walkthrough, for "walk me through it" and
     *  for the named ops_* scripts, which carry their own write confirms. */
    mode?: 'jump' | 'walk';
    /** The finding's own words, for the landing line. */
    findingTitle?: string;
    /** show = teach walkthrough; do = FG agent. */
    kind?: 'show' | 'do';
  };
  /** Stamped by the backend on write intents; the dock must confirm first. */
  confirmRequired?: boolean;
}

/** Executors may return a short result line for the dock to show as a Zana
 *  message (e.g. "Marked 3 alerts reviewed."). Navigate intents return void. */
export type ZanaIntentResult = string | void;
export type ZanaIntentExecutor = (intent: ZanaIntent) => ZanaIntentResult | Promise<ZanaIntentResult>;

const NAVIGATE_IDS: string[] = [
  'open_tab',
  'open_high_risk_alerts',
  'open_fuel_report',
  'open_bus',
  'open_zana_mode',
  'spotlight',
];
/** Zana takes the wheel. Not a navigate: it does not open one page and stop,
 *  it runs a whole sequence, so the dock owns its own indicator for it. */
const TAKEOVER_IDS: string[] = ['start_takeover'];
const READ_IDS: string[] = [
  'list_alerts_by_priority',
  'list_buses_missing_refuel',
  // A parent saying a drop-off was wrong. Read-only on purpose — Zana ranks
  // and presents them, a human resolves them.
  'list_parent_dropoff_flags',
  'list_unsigned_accounts',
  'export_monthly_report',
  // Ranged report (day / week / month / custom, PDF or CSV). Read, but in
  // ALWAYS_CONFIRM_IDS below: a download moves the fleet's data out of the
  // tenant, so it must never auto-run off the back of a sentence.
  'export_report',
  'list_report_schedules',
  'test_zana_connection',
  /* 💸 A read that costs money: it asks Google about roads this fleet has
     never driven. In ALWAYS_CONFIRM_IDS for that reason — her own trails are
     free and are the better answer whenever they exist. */
  'check_roads_with_google',
];
const WRITE_IDS: string[] = [
  'mark_alerts_reviewed',
  // Standing state that keeps acting after the conversation ends — the highest
  // bar for a confirm, not the lowest.
  'schedule_report',
  'cancel_report_schedule',
  // Mails every driver asking for a tank reading — a write in the truest sense:
  // it reaches real people outside the building.
  'request_fuel_readings',
  'flag_buses_missing_refuel',
  // Acknowledging is the only write that makes a DERIVED condition stay dealt
  // with. Flagging one raises it; this settles it.
  'ack_missing_refuel',
  'unack_missing_refuel',
  // Ranks the queue, keeps what needs a human, clears only the routine, emails
  // the shortlist. The safe replacement for bulk mark-reviewed in a takeover.
  'triage_high_risk',
  'draft_driver_coach_note',
  // Closes out incident reports on a bus. The other half of clearing High Risk:
  // the card counts incident history as well as unread alerts, so triage alone
  // could never take it to zero.
  'resolve_incidents',
  // Sends a real email to a real person. Confirm is not a formality here.
  'email_driver',
  'contact_late_bus_parents',
  'prepare_school_trip_draft',
  'invite_driver',
  'delete_bus',
  'create_bus',
  'suspend_bus',
  'suspend_driver',
  'apply_station_pump_price',
];

const TAB_LABELS: Record<string, string> = {
  dashboard: 'Open Dashboard',
  fleet: 'Open Fleet',
  accounts: 'Open Accounts',
  monthlyReport: 'Open Monthly Report',
  trips: 'Open Trips',
  liveMap: 'Open Live Map',
  analytics: 'Open Analytics',
  fuel: 'Open Fuel',
  fuelPrices: 'Open Fuel prices',
};

export function labelForZanaIntent(intent: ZanaIntent): string {
  switch (intent.actionId) {
    case 'open_tab':
      return TAB_LABELS[intent.params?.tab || ''] || `Open ${intent.params?.tab || 'tab'}`;
    case 'open_high_risk_alerts':
      return 'Open High Risk Alerts';
    case 'open_fuel_report':
      return 'Open Fuel Report';
    case 'open_zana_mode':
      return 'Open Zana Mode';
    case 'open_bus':
      return intent.params?.busId ? `Open ${intent.params.busId}` : 'Open bus';
    case 'spotlight':
      return intent.params?.busId || intent.params?.entityId
        ? `Spotlight ${intent.params.busId || intent.params.entityId}`
        : 'Spotlight';
    case 'start_takeover':
      return intent.params?.title || 'Walk me through it';
    case 'list_alerts_by_priority':
      return 'List alerts by priority';
    case 'list_buses_missing_refuel':
      return 'List buses missing refuel';
    case 'list_parent_dropoff_flags':
      return 'Show the drop-offs parents flagged';
    case 'check_roads_with_google':
      return intent.params?.plate
        ? `Check ${intent.params.plate}'s roads with Google`
        : 'Check these roads with Google';
    case 'mark_alerts_reviewed':
      return intent.params?.busId
        ? `Mark ${intent.params.busId} alerts reviewed`
        : 'Mark alerts reviewed';
    case 'flag_buses_missing_refuel':
      return 'Flag buses missing refuel';
    case 'triage_high_risk':
      return 'Triage and clear the routine ones';
    case 'resolve_incidents':
      return intent.params?.busId
        ? `Close out ${intent.params.busId}'s incidents`
        : 'Close out resolved incidents';
    case 'email_driver':
      return intent.params?.busId
        ? `Email ${intent.params.busId}'s driver`
        : "Email the bus's driver";
    case 'list_unsigned_accounts':
      return 'List accounts that have not signed up';
    case 'contact_late_bus_parents':
      return intent.params?.plate
        ? `Contact parents on ${intent.params.plate}`
        : 'Contact parents';
    case 'prepare_school_trip_draft':
      return 'Prepare this trip draft';
    case 'export_monthly_report':
      return intent.params?.month
        ? `Download ${intent.params.month} report`
        : 'Download monthly report';
    case 'export_report': {
      const p = intent.params || {};
      const fmt = String(p.format || 'pdf').toUpperCase();
      // Label the PERIOD, not just the verb — the chip is the last thing the
      // owner reads before a download, and "Download report" beside an answer
      // about last week is exactly how the month/week swap went unnoticed.
      const period = p.from && p.to
        ? (p.from === p.to ? p.from : `${p.from} to ${p.to}`)
        : String(p.preset || 'report');
      return `Download ${period} as ${fmt}`;
    }
    case 'schedule_report': {
      const p = intent.params || {};
      const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const at = `${String(p.hourUtc ?? 6).padStart(2, '0')}:00 UTC`;
      if (p.cadence === 'weekly') return `Email me every ${DAYS[Number(p.weekday ?? 1)] || 'Monday'} at ${at}`;
      if (p.cadence === 'monthly') return `Email me on day ${p.dayOfMonth ?? 1} each month at ${at}`;
      return `Email me every day at ${at}`;
    }
    case 'request_fuel_readings': {
      const only = intent.params?.onlyBuses;
      return Array.isArray(only) && only.length
        ? `Ask ${only.length} driver${only.length === 1 ? '' : 's'} for a fuel reading`
        : 'Ask my drivers for a fuel reading';
    }
    case 'list_report_schedules':
      return 'Show my scheduled reports';
    case 'cancel_report_schedule':
      return 'Stop this scheduled report';
    case 'invite_driver':
      return 'Invite selected driver';
    case 'delete_bus':
      return intent.params?.busId ? `Delete ${intent.params.busId}` : 'Delete bus';
    case 'create_bus':
      return intent.params?.plate ? `Create ${intent.params.plate}` : 'Create bus';
    case 'suspend_bus':
      return intent.params?.busId ? `Suspend ${intent.params.busId}` : 'Suspend bus';
    case 'suspend_driver':
      return intent.params?.driverLabel || intent.params?.accountQuery || intent.params?.driverId
        ? `Suspend ${intent.params.driverLabel || intent.params.accountQuery || intent.params.driverId}`
        : 'Suspend driver';
    case 'apply_station_pump_price': {
      const name = intent.params?.stationName;
      const p = intent.params?.pricePerL;
      if (name && p) return `Put ${name} on the list at Ksh ${Number(p).toFixed(2)}/L`;
      if (name) return `Put ${name}'s pump price on the list`;
      return 'Put this pump price on the station list';
    }
    case 'ack_missing_refuel':
      return intent.params?.busId
        ? `Park ${intent.params.busId} — I know about it`
        : 'Park these — I know about them';
    case 'unack_missing_refuel':
      return 'Un-park and show them again';
    case 'draft_driver_coach_note':
      return intent.params?.driverLabel || intent.params?.driverId
        ? `Draft coach note for ${intent.params.driverLabel || intent.params.driverId}`
        : 'Draft driver coach note';
    case 'test_zana_connection':
      return 'Test Zana AI';
    default:
      return 'Open';
  }
}

export function isNavigateIntent(intent: ZanaIntent): boolean {
  return NAVIGATE_IDS.includes(String(intent.actionId));
}

export function isReadIntent(intent: ZanaIntent): boolean {
  return READ_IDS.includes(String(intent.actionId));
}

export function isWriteIntent(intent: ZanaIntent): boolean {
  return WRITE_IDS.includes(String(intent.actionId));
}

export function isTakeoverIntent(intent: ZanaIntent): boolean {
  return TAKEOVER_IDS.includes(String(intent.actionId));
}

/** Anything the dock is allowed to execute at all. */
export function isExecutableIntent(intent: ZanaIntent): boolean {
  return isNavigateIntent(intent) || isReadIntent(intent) || isWriteIntent(intent)
    || isTakeoverIntent(intent);
}

/**
 * Actions that always confirm despite being classified read-only.
 *
 * A CSV export reads nothing it should not, so the server's risk model calls it
 * a read — but it moves the whole fleet's data OUT of the tenant onto someone's
 * laptop, which is a decision, not a lookup. Zana's own answer already told the
 * owner to "confirm below"; because the flag derives purely from the write
 * list, no confirm control could ever render, and the promise was structurally
 * a lie. The answer was right about the risk and the classification was wrong.
 */
/* Reads that must still be asked for. A report leaves the tenant; a Google
   road check spends money. Neither may run off the back of a sentence. */
const ALWAYS_CONFIRM_IDS = new Set<string>(['export_monthly_report', 'export_report', 'check_roads_with_google']);

/** Writes always confirm — even if a buggy payload forgot the flag. */
export function intentNeedsConfirm(intent: ZanaIntent): boolean {
  return isWriteIntent(intent)
    || ALWAYS_CONFIRM_IDS.has(String(intent.actionId))
    || intent.confirmRequired === true;
}

/**
 * Live dashboard is the source of truth for the unread queue. Drop mark-reviewed
 * chips when there is nothing to mark — but keep other writes (flag missing
 * refuel, draft coach note) available.
 */
export function filterIntentsForLiveAlerts(
  intents: ZanaIntent[] | undefined | null,
  liveUnreadCount: number,
): ZanaIntent[] {
  const list = Array.isArray(intents) ? intents : [];
  if (liveUnreadCount > 0) return list.filter(isExecutableIntent);
  return list.filter(
    (i) => isExecutableIntent(i) && i.actionId !== 'mark_alerts_reviewed',
  );
}

/** Dedupe by actionId (first wins), cap at 3. */
function dedupeIntents(intents: ZanaIntent[]): ZanaIntent[] {
  const seen = new Set<string>();
  const out: ZanaIntent[] = [];
  for (const i of intents) {
    if (!i || !isExecutableIntent(i)) continue;
    const key = `${i.actionId}:${i.params?.tab || ''}:${i.params?.busId || ''}:${i.params?.driverId || ''}:${i.params?.scriptId || ''}:${i.params?.month || ''}:${i.params?.inviteRef || ''}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(i);
    if (out.length >= 3) break;
  }
  return out;
}

/**
 * Map free-text briefing / insight copy onto allow-listed intents so banner
 * and tab bands can offer the same chips as Ask Zana (Phase 3 / 5).
 */
export function intentsFromInsightText(
  text: string | null | undefined,
  opts?: {
    liveUnreadCount?: number;
    /** Always include Open Zana Mode (health banner primary CTA). */
    includeZanaMode?: boolean;
    /** Analytics sub-tab context — adds a sensible default surface chip. */
    tab?: string;
    /** When true, add money-leak chips (missing-refuel list/flag). */
    moneyLeak?: boolean;
    hasMissingRefuel?: boolean;
  },
): ZanaIntent[] {
  const q = String(text || '').toLowerCase();
  const unread = opts?.liveUnreadCount ?? 0;
  const out: ZanaIntent[] = [];

  if (/high[- ]?risk|unread alert|\balerts?\b|mark .*reviewed|incidents?/.test(q)) {
    out.push({ actionId: 'open_high_risk_alerts', params: {} });
    if (unread > 0) {
      out.push({ actionId: 'mark_alerts_reviewed', params: {}, confirmRequired: true });
    }
  }
  if (/fuel|refuel|no refill|siphon|litre|liter|diesel|petrol|economy|km\/l|money|cost|spend|leak|waste/.test(q)
    || opts?.moneyLeak) {
    out.push({ actionId: 'open_fuel_report', params: {} });
    if (opts?.hasMissingRefuel || /no refill|no refuel|missing refuel/.test(q)) {
      out.push({ actionId: 'list_buses_missing_refuel', params: {} });
      out.push({ actionId: 'flag_buses_missing_refuel', params: {}, confirmRequired: true });
      out.push({ actionId: 'ack_missing_refuel', params: {}, confirmRequired: true });
    }
  }
  if (/drop[- ]?off|dropped|parent (said|says|flag)|wrong stop|not right|collected/.test(q)) {
    out.push({ actionId: 'list_parent_dropoff_flags', params: {} });
    out.push({ actionId: 'open_high_risk_alerts', params: {} });
  }
  if (/live map|gps|where (is|are)|track(ing)?\b/.test(q)) {
    out.push({ actionId: 'open_tab', params: { tab: 'liveMap' } });
  }
  if (/driver|coach|rating|stars/.test(q)) {
    out.push({ actionId: 'open_tab', params: { tab: 'analytics' } });
  }
  if (/route|corridor|delay|late|trip/.test(q) && !/school trip/.test(q)) {
    out.push({ actionId: 'open_zana_mode', params: {} });
  }

  // Tab defaults when the insight line is thin.
  if (opts?.tab === 'fuel' && !out.some((i) => i.actionId === 'open_fuel_report')) {
    out.push({ actionId: 'open_fuel_report', params: {} });
  }
  if (opts?.tab === 'incidents' && !out.some((i) => i.actionId === 'open_high_risk_alerts')) {
    out.push({ actionId: 'open_high_risk_alerts', params: {} });
  }
  if ((opts?.tab === 'routes' || opts?.tab === 'drivers') && !out.some((i) => i.actionId === 'open_zana_mode')) {
    out.push({ actionId: 'open_zana_mode', params: {} });
  }

  if (opts?.includeZanaMode) {
    out.push({ actionId: 'open_zana_mode', params: {} });
  }

  return filterIntentsForLiveAlerts(dedupeIntents(out), unread);
}
